// PRD §3.1.2 — Sub-Category (하위영역) detail panel. Slice 6 ships the full
// editable form:
//   - Top: numbered badge + heading + top-right [Delete] (WRITE only).
//   - Breadcrumb back to the parent Domain.
//   - Controlled name (required) + description inputs.
//   - Items mini-table with clickable rows (jumps to the item, expanding the
//     tree along the way).
//   - Bottom: [+ Add Item] and [Save] actions (WRITE only).
//
// Item detail rendering itself (the right-panel form) lands in Slices 7+; for
// now `+ Add Item` selects the new item which falls back to `EmptyDetail`.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Folder, Plus, Trash2 } from 'lucide-react';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';
import { useDeployDirtyStore } from '@/features/pro-deployment/store/deploy-dirty.store';
import type {
  ProDomain,
  ProItemDesignApiError,
  ProSubArea,
} from '../types/pro-item-design.types';
import {
  domainKey,
  nextTempId,
  subAreaKey,
  useProItemDesignPageStore,
} from '../store/pro-item-design-page.store';
import {
  useCreateSubArea,
  useDeleteSubArea,
  useUpdateSubArea,
} from '../hooks/use-pro-mutations';
import { domainNumber, subAreaNumber } from '../utils/numbering';
import { isDraftId } from '../utils/draft-node';
import { ProStatusBadge } from './ProStatusBadge';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

interface SubAreaDetailProps {
  domain: ProDomain;
  domainIndex: number;
  subArea: ProSubArea;
  subAreaIndex: number;
}

export function SubAreaDetail({
  domain,
  domainIndex,
  subArea,
  subAreaIndex,
}: SubAreaDetailProps) {
  const { t } = useTranslation('pro-item-design');
  const { canWrite, denyProps, tooltip } = useWriteGuard('PRO_MGMT');
  const select = useProItemDesignPageStore((s) => s.select);
  const requestSelect = useProItemDesignPageStore((s) => s.requestSelect);
  const setExpanded = useProItemDesignPageStore((s) => s.setExpanded);
  const setDirty = useProItemDesignPageStore((s) => s.setDirty);
  const requestFocus = useProItemDesignPageStore((s) => s.requestFocus);
  const focusRequestId = useProItemDesignPageStore((s) => s.focusRequestId);
  const consumeFocus = useProItemDesignPageStore((s) => s.consumeFocus);
  const startDraft = useProItemDesignPageStore((s) => s.startDraft);
  const clearDraft = useProItemDesignPageStore((s) => s.clearDraft);
  const nameRef = useRef<HTMLInputElement>(null);
  // SHCT-388 — a not-yet-persisted Sub-area (negative id) creates on Save.
  const isDraft = isDraftId(subArea.id);

  const [name, setName] = useState(subArea.name);
  const [description, setDescription] = useState(subArea.description ?? '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [afterDeleteOpen, setAfterDeleteOpen] = useState(false);
  const markItemDirty = useDeployDirtyStore((s) => s.addItem);

  useEffect(() => {
    setName(subArea.name);
    setDescription(subArea.description ?? '');
    setNameError(null);
    setDirty(false);
  }, [subArea.id, subArea.name, subArea.description, setDirty]);

  // QC PID-013 — focus the name input when this Sub-area was just created.
  useEffect(() => {
    if (focusRequestId === subArea.id) {
      nameRef.current?.focus();
      nameRef.current?.select();
      consumeFocus(subArea.id);
    }
  }, [focusRequestId, subArea.id, consumeFocus]);

  const createMutation = useCreateSubArea();
  const updateMutation = useUpdateSubArea();
  const deleteMutation = useDeleteSubArea();
  const dn = domainNumber(domainIndex);
  const sn = subAreaNumber(domainIndex, subAreaIndex);

  const onSelectParent = () => {
    // Breadcrumb crumb is user-initiated → guarded (Slice 13).
    requestSelect({ type: 'domain', id: domain.id });
  };

  const onSelectItem = (itemId: number) => {
    // Mini-table row click is user-initiated → guarded (Slice 13).
    setExpanded(domainKey(domain.id), true);
    setExpanded(subAreaKey(subArea.id), true);
    requestSelect({
      type: 'item',
      id: itemId,
      domainId: domain.id,
      subAreaId: subArea.id,
    });
  };

  const onSaveError = (e: unknown) => {
    const err = e as ProItemDesignApiError;
    // §7.1 — field-scoped errors stay INLINE in this form; only non-field
    // (System) errors toast.
    if (err?.fields?.name) {
      setNameError(err.fields.name);
      return;
    }
    toast.error(err?.message || t('toasts.saveFailed'));
  };

  const onSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t('subArea.validation.nameRequired'));
      return;
    }
    const input = { name: trimmed, description: description.trim() || null };
    if (isDraft) {
      // SHCT-388 — first Save persists the draft (createSubArea) under its
      // parent domain, then selects the real node and drops the draft.
      createMutation.mutate(
        { domainId: domain.id, input },
        {
          onSuccess: (created) => {
            setDirty(false);
            clearDraft();
            setExpanded(domainKey(domain.id), true);
            setExpanded(subAreaKey(created.id), true);
            select({ type: 'subArea', id: created.id, domainId: domain.id });
            toast.success(t('toasts.saved'));
          },
          onError: onSaveError,
        },
      );
      return;
    }
    updateMutation.mutate(
      { id: subArea.id, input },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success(t('toasts.saved'));
        },
        onError: onSaveError,
      },
    );
  };

  const onConfirmDelete = () => {
    if (isDraft) {
      // SHCT-388 — a draft Sub-area was never persisted; delete just discards it.
      setConfirmOpen(false);
      clearDraft();
      select(null);
      return;
    }
    // PRD §3.4 — a Sub-area delete cascades to its items. Flag any PUBLISHED
    // item dirty for PRO Deployment and show the deploy guidance.
    const publishedPids = subArea.items
      .filter((it) => it.status === 'PUBLISHED')
      .map((it) => it.pid);
    deleteMutation.mutate(subArea.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        toast.success(t('toasts.deleted'));
        if (publishedPids.length > 0) {
          publishedPids.forEach(markItemDirty);
          setAfterDeleteOpen(true);
        } else {
          select(null);
        }
      },
      onError: (e) => {
        setConfirmOpen(false);
        const err = e as ProItemDesignApiError;
        toast.error(err?.message || t('toasts.deleteFailed'));
      },
    });
  };
  const onAfterDeleteClose = () => {
    setAfterDeleteOpen(false);
    select(null);
  };

  // SHCT-388 — park a draft item; persisted on its Save. Disabled while this
  // Sub-area is itself an unsaved draft (a child needs a real parent id).
  const onAddItem = () => {
    const tempId = nextTempId();
    startDraft({
      type: 'item',
      tempId,
      domainId: domain.id,
      subAreaId: subArea.id,
      name: t('defaults.newItemName'),
    });
    setExpanded(domainKey(domain.id), true);
    setExpanded(subAreaKey(subArea.id), true);
    select({ type: 'item', id: tempId, domainId: domain.id, subAreaId: subArea.id });
    requestFocus(tempId);
  };

  return (
    <>
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="badge badge-primary" style={{ fontSize: 13, fontWeight: 700 }}>
            {sn}
          </span>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
            {t('subArea.heading')}
          </h2>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          style={{ color: 'var(--destructive)' }}
          onClick={() => setConfirmOpen(true)}
          disabled={!canWrite || deleteMutation.isPending}
          {...denyProps}
        >
          <Trash2 size={14} />
          {t('actions.delete')}
        </button>
      </div>

      <div style={{ padding: 24 }}>
        <div
          style={{
            fontSize: 11,
            color: 'var(--muted-foreground)',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            // #2 — allow long Category / Sub-category names to wrap instead of
            // overflowing the panel and pushing the layout out of shape.
            flexWrap: 'wrap',
          }}
        >
          <Folder size={12} style={{ flexShrink: 0 }} />
          <button
            type="button"
            onClick={onSelectParent}
            className="link-text"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'inherit',
              font: 'inherit',
              textAlign: 'left',
              minWidth: 0,
              wordBreak: 'break-word',
            }}
          >
            {dn}. {domain.name}
          </button>
          <ChevronRight size={12} style={{ flexShrink: 0 }} />
          <span style={{ minWidth: 0, wordBreak: 'break-word' }}>
            {sn}. {subArea.name}
          </span>
        </div>

        <div className="form-field">
          <label className="form-field__label" htmlFor="subarea-name">
            {t('subArea.fields.name.label')}{' '}
            <span className="required" aria-label={t('subArea.fields.name.required')}>
              *
            </span>
          </label>
          <input
            id="subarea-name"
            ref={nameRef}
            type="text"
            className={
              nameError ? 'form-field__input form-field__input--error' : 'form-field__input'
            }
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameError(null);
              setDirty(true);
            }}
            placeholder={t('subArea.fields.name.placeholder')}
            maxLength={200}
            readOnly={!canWrite}
            {...denyProps}
          />
          {nameError ? <p className="form-field__error">{nameError}</p> : null}
        </div>
        <div className="form-field">
          <label className="form-field__label" htmlFor="subarea-description">
            {t('subArea.fields.description.label')}
          </label>
          <textarea
            id="subarea-description"
            className="form-field__textarea"
            rows={3}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setDirty(true);
            }}
            maxLength={1000}
            readOnly={!canWrite}
            {...denyProps}
          />
        </div>

        <div className="section-divider" />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {t('subArea.itemList.title', { count: subArea.items.length })}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={onAddItem}
            disabled={!canWrite || isDraft}
            {...denyProps}
            // See DomainDetail: permission outranks the draft hint, and `tooltip` has to be
            // named explicitly because this `title` overrides the one `{...denyProps}` sets.
            title={tooltip ?? (isDraft ? t('subArea.saveBeforeAddItem') : undefined)}
          >
            <Plus size={14} />
            {t('actions.addItem')}
          </button>
        </div>
        <table className="table table--clickable">
          <thead>
            <tr>
              <th style={{ width: 60 }}>{t('subArea.itemList.columns.number')}</th>
              <th>{t('subArea.itemList.columns.name')}</th>
              <th style={{ width: 100 }}>{t('subArea.itemList.columns.schedule')}</th>
              <th style={{ width: 80 }}>{t('subArea.itemList.columns.status')}</th>
            </tr>
          </thead>
          <tbody>
            {subArea.items.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  style={{
                    textAlign: 'center',
                    color: 'var(--muted-foreground)',
                    padding: 24,
                  }}
                >
                  {t('subArea.itemList.empty')}
                </td>
              </tr>
            ) : (
              subArea.items.map((it, ii) => (
                <tr
                  key={it.id}
                  onClick={() => onSelectItem(it.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ color: 'var(--info)', fontWeight: 600 }}>
                    {sn}-{ii + 1}
                  </td>
                  <td>
                    {it.name}
                    <span
                      style={{
                        fontSize: 10,
                        color: 'var(--muted-foreground)',
                        marginLeft: 6,
                      }}
                    >
                      {it.pid}
                    </span>
                  </td>
                  <td>{it.scheduleSummary ?? '-'}</td>
                  <td>
                    <ProStatusBadge status={it.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 24,
          }}
        >
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={onSave}
            disabled={!canWrite || createMutation.isPending || updateMutation.isPending}
            {...denyProps}
          >
            {createMutation.isPending || updateMutation.isPending
              ? t('actions.saving')
              : t('actions.save')}
          </button>
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={t('subArea.delete.title')}
        message={t('subArea.delete.message')}
        confirmLabel={t('subArea.delete.confirm')}
        cancelLabel={t('actions.cancel')}
        confirmVariant="destructive"
        loading={deleteMutation.isPending}
        loadingLabel={t('actions.deleting')}
        onConfirm={onConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* PRD §3.4 — Post-delete PRO Deployment guidance, shown when the deleted
          Sub-area cascaded away one or more PUBLISHED items. */}
      <InfoModal
        open={afterDeleteOpen}
        title={t('item.afterDelete.title')}
        body={t('item.afterDelete.message')}
        confirmLabel={t('actions.confirm')}
        onClose={onAfterDeleteClose}
      />
    </>
  );
}
